import { Dialog } from '../../../components/dialog-portal/dialog.tsx';

export function AutoTasksInfoDialog({ onClose }: { onClose: () => void }) {
    return (
        <Dialog title='Auto Tasks Info' onClose={onClose}>
            <div style={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto', paddingRight: 6 }}>
                <h3>What This Table Shows</h3>
                <p>The table shows live automatic tasks per equipment. Data is refreshed every 1 second from the backend.</p>

                <h3>Columns</h3>
                <p>Equipment name: machine coordinates and display name. Clicking it opens machine log.</p>
                <p>
                    Part number: part display name. The moon icon is shown when produceAtNight is true for the current part/product. This is a product
                    flag, not the same thing as required-this-night.
                </p>
                <p>
                    Time remaining: target time minus now. Negative means overdue. For tasks that are not up yet, negative values are clamped to avoid
                    showing overdue before target is reached.
                </p>
                <p>Notes: task kind and details such as variant/pcs for measure and xN for combined tool changes.</p>

                <h3>Row Colors</h3>
                <p>Orange row: active issue style (help, disconnected, or work counter/tool change that is up).</p>
                <p>Blue row: required-this-night task shown during evening mode.</p>

                <h3>When Tasks Appear</h3>
                <p>A task is visible when any of these conditions is true:</p>
                <ul>
                    <li>Task target time is within 30 minutes (or overdue).</li>
                    <li>Evening mode is on and task is marked show-early-this-evening.</li>
                    <li>Evening mode is on and task is marked required-this-night.</li>
                    <li>Help tasks are always visible.</li>
                </ul>

                <h3>Night Flags Explained</h3>
                <ul>
                    <li>
                        showEarlyThisEvening: true for produce-at-night toolChange tasks with target time in the evening window (18:00-22:00). It
                        makes them visible already in the evening.
                    </li>
                    <li>
                        isRequiredThisNight: true for produce-at-night workCounter or toolChange tasks with target time in the night window
                        (22:00-06:00 next day).
                    </li>
                </ul>
                <p>
                    Blue highlight uses isRequiredThisNight together with evening mode. A task can have the moon icon without being blue, and can be
                    blue only when the required-this-night condition is true.
                </p>

                <h3>How Tasks Are Created (Counter State to Tasks)</h3>
                <p>Backend builds a counter state per equipment from live machine data, batch details, protocol, alarms, and projections.</p>
                <p>That counter state is transformed into auto tasks of type: measure, help, disconnected, workCounter, and toolChange.</p>
                <p>Key mapping:</p>
                <ul>
                    <li>Measure task: created from interval due projection.</li>
                    <li>Work counter task: created from work counter projection/alarm.</li>
                    <li>Tool change task: created from each tool-change projection; nearby tool changes are combined.</li>
                    <li>Help task: created when stop status is helpMe.</li>
                    <li>Disconnected task: created when machine status indicates disconnect.</li>
                </ul>

                <h3>Night Logic and Constraints</h3>
                <ul>
                    <li>Evening mode starts at 18:00 (Europe/Helsinki).</li>
                    <li>Evening window: 18:00-22:00.</li>
                    <li>Night window: 22:00-06:00 next day.</li>
                    <li>Only produce-at-night parts can get evening/night flags.</li>
                    <li>showEarlyThisEvening applies only to toolChange tasks in the evening window.</li>
                    <li>isRequiredThisNight applies to workCounter and toolChange tasks in the night window.</li>
                </ul>

                <h3>Constants Used</h3>
                <ul>
                    <li>Evening start hour: 18.</li>
                    <li>Night range: 22:00-06:00.</li>
                    <li>Visibility near-due threshold: 30 minutes.</li>
                    <li>Tool-change combine window: less than 1 hour between target times.</li>
                    <li>Frontend auto-task refresh interval: 1 second.</li>
                </ul>
            </div>
        </Dialog>
    );
}
